/**
 * Third-party fulfilment, phase 2 step 3 — the shapes the catalogue item page,
 * the catalogue editor and the estimate pages render once supplier-backed
 * prices can reach a quote (product brief
 * docs/features/catalogue-third-party-fulfilment/07_phase_2_product_brief.md,
 * decisions #82–#90; spec 12).
 *
 * The frontend's camelCase view of the generated wire schemas
 * (`PriceOptionsSchema`, `RouteNarrowing`), mapped in `api/catalogue.ts` the
 * same way `CatalogueItemPricePreview` is.
 *
 * Vocabulary (#44, N, #83): suppliers **quote**, we pay a **cost**, we charge
 * a **price**. A fulfilment route's **route class** is in-house or bought-in.
 * The supplier behind an option is shown to staff only (#79); nothing here is
 * ever serialised to a customer-facing surface (#9).
 */

import type { OptionArrival } from "./speed-types";

/** Whether a fulfilment route is made by us or bought in (#83). */
export type RouteClass = "in_house" | "bought_in";

/** The in-house route's code, as the backend's `IN_HOUSE_ROUTE_CODE` spells it. */
export const IN_HOUSE_ROUTE = "in_house";

/**
 * How delivery reads on one price option (#86).
 *
 * - `priced` — delivery priced by us at the chosen address.
 * - `at_address` — no address yet, so delivery is not priced yet.
 * - `not_priced` — the address is known and no price came: the route is no
 *   option (order to invoice doc 107), so this appears only while no option
 *   is priced.
 * - `included` — the supplier's price includes delivery.
 * - `none` — collection or hold: nothing is shipped.
 */
export type PriceOptionDelivery =
  | { kind: "priced"; deliveryPricePence: number }
  | { kind: "at_address" }
  | { kind: "not_priced" }
  | { kind: "included" }
  | { kind: "none" };

/** One price option: a (fulfilment route, tier) pair at the quoted quantity (#21.2). */
export interface CataloguePriceOption {
  /** `${routeCode}:${tier}` — stable within one preview. */
  key: string;
  routeCode: string;
  routeClass: RouteClass;
  /** "In-house" or the supplier's name. Staff surfaces only (#79). */
  routeLabel: string;
  /** The route's turnaround tier: its time file's (in-house) or the supplier's own name. */
  tier: string;
  /** When it arrives, projected by Time; null exactly when `noArrivalReason` says why. */
  arrival: OptionArrival | null;
  /** The fastest Speed the arrival meets; null when slower than every Speed or with no date. */
  speedCode: string | null;
  noArrivalReason: string | null;
  /** The print price for this option, ex VAT. Includes delivery when `delivery.kind === "included"`. */
  pricePence: number;
  /** What this option costs us. Null when the caller may not see cost. */
  costPence: number | null;
  /** Contribution as a percentage of `pricePence`. Null with `costPence`. */
  contributionPct: number | null;
  delivery: PriceOptionDelivery;
  /**
   * The figure the sourcing policy compares (#85): the price plus delivery.
   * `null` when delivery is not priced yet, which is exactly when the default
   * can only be provisional.
   */
  comparablePence: number | null;
  /** A supplier cost inside its TTL but not refreshed last night (#35). */
  isExpiring: boolean;
  /** Arrives by the needed-by date (true without one). */
  arrivesInTime: boolean;
  /**
   * Staff warning that this supplier option's price list is running out or has
   * run out ("SG World's list ends 31 Jan"). Absent or null otherwise. The
   * option keeps its price; this only tells the CSR it may be out of date.
   */
  listNotice?: string | null;
  /**
   * A line of several versions priced as one price per version (order to invoice doc 108): each version's quantity,
   * in order. `pricePence` and `costPence` are then the sum over the versions. Absent for one version.
   */
  versionQuantities?: number[];
  /**
   * How the route makes those versions: `separate` (one run per version) or `combined` (one run for the line, as
   * in-house does). Absent for one version.
   */
  versionPrices?: "separate" | "combined";
}

/** A route that could have been offered but is not, and why (journey A). */
export interface UnavailableRoute {
  routeCode: string;
  routeLabel: string;
  reason: string;
  /** Withheld because its delivery isn't priced (doc 107): `reason` is the
   *  whole warning, and an agreed delivery price makes the route an option. */
  isDeliveryNotPriced: boolean;
}

/**
 * The price options (the routes and tiers behind the Speeds) for one
 * configuration and quantity. Absent on the price preview when the server lists
 * no route options (#90): the page lists the Speeds alone.
 */
export interface CataloguePriceOptions {
  options: CataloguePriceOption[];
  /** The sourcing policy's pick (#85). */
  defaultKey: string;
  /** One line, plain English: why the default is the default. */
  defaultReason: string;
  /** True before the delivery address is set: in-house by rule, re-evaluated later. */
  isProvisional: boolean;
  /** The option the headline price was built from: the request's, else the selected Speed's. */
  selectedKey: string;
  unavailable: UnavailableRoute[];
  /** The CSR's chosen option is no longer offered; the default was selected instead (journey C). */
  chosenWasDropped: boolean;
  /**
   * What Send / Place order need because the selected option is not the
   * default (#2), or null when it is. The backend decides; the page holds
   * Send until it is met, and the 400 on submit stays the backstop.
   */
  overrideRequirement: RouteOverrideRequirement | null;
  /** Live supplier routes still pricing when the wait ran out: neither options nor unavailable (live pricing §6.2). */
  pending: PendingPriceRoute[];
}

/** A live supplier route still pricing: ask again after `retryAfterSeconds`; its answer is kept when it comes. */
export interface PendingPriceRoute {
  routeCode: string;
  routeLabel: string;
  retryAfterSeconds: number;
}

/** The backend's statement of what an override needs before it can be sent (#2). */
export interface RouteOverrideRequirement {
  /** Shown while no reason is chosen. */
  reasonPrompt: string;
  /** The reasons that also need a note (today: Other). */
  noteRequiredFor: RouteOverrideReason[];
  /** Shown while such a reason has no note. */
  notePrompt: string;
  /** Set when the option was chosen while a route was still pricing: the reason and note to start from. */
  suggestedReason: RouteOverrideReason | null;
  suggestedNote: string | null;
}

/** The choice a CSR makes on the catalogue item page, sent back with the preview request. */
export interface PriceOptionChoice {
  routeCode: string;
  tier: string;
}

/** Why a CSR chose an option other than the default (#2). Follows `PriceAdjustmentReason`. */
export type RouteOverrideReason =
  | "earlier_date"
  | "quality_or_specialism"
  | "keep_in_house"
  | "known_problem_with_default"
  | "customer_asked"
  | "other";

export const ROUTE_OVERRIDE_REASONS: ReadonlyArray<{
  value: RouteOverrideReason;
  label: string;
}> = [
  { value: "earlier_date", label: "Customer needs an earlier date" },
  { value: "quality_or_specialism", label: "Quality or specialism" },
  {
    value: "keep_in_house",
    label: "Keep the work in-house (capacity or relationship)",
  },
  {
    value: "known_problem_with_default",
    label: "A known problem with the default route",
  },
  { value: "customer_asked", label: "The customer asked for it" },
  { value: "other", label: "Other" },
];

/**
 * Which fulfilment routes and Speeds a customer catalogue or catalogue item
 * allows (#87, d63). Same moves as option narrowing: restrict to a subset, or
 * pin to one. Empty `allowedRoutes` and no pin = every route the dictionary
 * item has; no `allowedSpeeds` = every Speed (one code fixes the Speed).
 */
export interface RouteNarrowing {
  allowedRoutes?: string[];
  pinnedRoute?: string | null;
  allowedSpeeds?: string[] | null;
}

// ── The purchase order and the Fulfilment route card (phase 2 step 5) ──────
// Journey E: the CSR places the order in the supplier's own UI and records it
// here (#80's manual form). Mapped from `EstimateFulfilmentSchema` in
// `api/catalogue.ts`. Staff only: this names the supplier (#79).
